import { Injectable } from '@angular/core';
import { Auth, User, authState, signInWithPopup, GoogleAuthProvider, OAuthProvider, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from '@angular/fire/auth';
import { Firestore, doc, docData, setDoc, updateDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Platform, ToastController } from '@ionic/angular/standalone';
// import { SignInWithApple, ASAuthorizationAppleIDRequest, AppleSignInResponse, AppleSignInErrorResponse } from "@ionic-native/sign-in-with-apple/ngx";
// import { GooglePlus } from '@awesome-cordova-plugins/google-plus/ngx';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
// import { MessagingService } from './messaging.service';
// import { NotifyService } from './notify.service';
import { Profile } from '../models/profile.interface';
import { environment } from '../../environments/environment';
import { User as UserInterface } from '../models/user.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: Observable<UserInterface | null | undefined>;
  message: any;
  alias: string = '';
  initials: string = '';
  tokens: any = {};
  welcome: boolean = false;
  isAppleLogin: boolean = false;
  isGoogleLogin: boolean = false;
  webClientID: string = environment.webClientID;
  
  constructor(
    private auth: Auth,
    private firestore: Firestore,
    // private googleplus: GooglePlus,
    // private ms: MessagingService,
    // private ns: NotifyService,
    private platform: Platform,
    private router: Router,
    // private signInWithApple: SignInWithApple,
    private toastCtrl: ToastController,
  ) {
      this.user = authState(this.auth).pipe(
        switchMap(user => {
          if (user) {
            return docData(doc(this.firestore, `ge_users/${user.uid}`)) as Observable<UserInterface>;
          } else {
            return of(null);
          }
        }
      )
    )
  }

  ////// OAuth Methods /////

  async appleLogin() {
    if (this.platform.is('cordova')) {
      // Mobile implementation if needed
      return;
    } else {
      try {
        const provider = new OAuthProvider('apple.com');
        const credential = await signInWithPopup(this.auth, provider);
        this.isAppleLogin = true;
        await this.presentToast('Welcome!', 'success');
        if (credential.user) {
          await this.updateUserData(credential.user);
          return this.router.navigate(['/account/dashboard']);
        }
        return;
      } catch (error: any) {
        this.handleError(error);
        return;
      }
    }
  }

  async googleLogin() {
    if (this.platform.is('cordova')) {
      // Handle mobile login if needed
      return;
    } else {
      const provider = new GoogleAuthProvider();
      try {
        const credential = await signInWithPopup(this.auth, provider);
        this.isGoogleLogin = true;
        await this.presentToast('Welcome!', 'success');
        if (credential.user) {
          await this.updateUserData(credential.user);
          return this.router.navigate(['/account/dashboard']);
        }
        return;
      } catch (error) {
        console.error(error);
        return;
      }
    }
  }

  async emailSignUp(email: string, password: string) {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      if (credential.user) {
        await sendEmailVerification(credential.user);
        await this.presentToast('Check your email to complete verification!', 'success');
        await this.updateUserData(credential.user);
        return this.router.navigate(['/account/profile']);
      }
      return;
    } catch (error: any) {
      this.handleError(error);
      return;
    }
  }

  async emailLogin(email: string, password: string) {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      if (credential.user) {
        await this.presentToast('Welcome!', 'success');
        await this.updateUserData(credential.user);
        return this.router.navigate(['/account/dashboard']);
      }
      return;
    } catch (error: any) {
      this.handleError(error);
      return;
    }
  }

  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(this.auth, email);
      await this.presentToast('Password update email sent', 'success');
    } catch (error: any) {
      this.handleError(error);
    }
  }

  async signOut() {
    await signOut(this.auth);
    this.isAppleLogin = false;
    this.isGoogleLogin = false;
    await this.router.navigate(['/']);
  }

  private handleError(error: Error) {
    this.presentToast(error.message, 'danger');
  }

  private checkUser() {
    this.user.subscribe(userData => {
      if (userData) {
        if (userData.push == 'disabled') {
          this.tokens = {};
        } else if (userData.tokens) {
          this.tokens = userData.tokens;
          if (this.platform.is('cordova')) {
            try {
              // this.ns.grantPermission(userData);
              // this.ns.notificationSetup(userData);
            } catch (err) {
              // Handle error
            }
          } else {
            try {
              // this.ms.requestPermission(userData);
              // this.ms.receiveMessage();
              // this.message = this.ms.currentMessage;
            } catch (err) {
              // Handle error
            }
          }
        }
      }
    });
  }

  async updateUserData(user: User) {
    const userRef = doc(this.firestore, 'ge_users', user.uid);
    const data: UserInterface = {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      tokens: this.tokens
    };
    await setDoc(userRef, data, { merge: true });
    this.checkUser();
  }

  async presentToast(message: string, type: string) {
    const toast = await this.toastCtrl.create({
      message: message,
      color: type,
      position: 'bottom',
      duration: 2000
    });
    await toast.present();
  }

  getUserIdToken(): Observable<string> {
    return authState(this.auth).pipe(
      filter((user): user is User => user !== null),
      switchMap(user => user.getIdToken())
    );
  }
 
  getProfile(id: string) {
    return docData(doc(this.firestore, 'ge_users', id));
  }
}